import { useQueryClient } from '@tanstack/react-query';
import { FileSpreadsheet } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import {
  CRITERION_IMPORTANCE_LABELS,
  IMPORT_TARGET_LABELS,
  IMPORT_TARGETS,
  coverageNote,
  isImportTarget,
  mappingProblems,
  normaliseHeader,
  type ImportCoverageRow,
  type ImportTarget,
  type ImportView,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Field } from '../../../components/Field';
import { ModuleGate } from '../../../components/ModuleGate';
import { Select } from '../../../components/Select';
import { ToneChip } from '../../../components/Tone';
import { EditableTable, type EditableColumn } from '../../../components/editable-table/EditableTable';
import { useSourceOptions } from '../../../lib/app-settings';
import { formatNumber } from '../../../lib/format';
import { useToasts } from '../../../stores/toasts';
import { createSaveLane, type SaveLane } from '../../jobs/hooks/save-lane';
import { chooseImportSheet, discardImport, importKey, IMPORTS_KEY, saveImportMapping, startImport, type MappingChange } from '../api/imports-api';
import { IMPORT_STATUS_LABELS, IMPORT_STATUS_TONES } from './import-status';
import { PreviewPeople } from './PreviewPeople';
import styles from './imports.module.css';

// IMP-01 after the upload (§L29, F22.02–F22.03): match each column to a field, say where each
// criterion is read from (DECISIONS-LOG "Start R4" Q4), look at the first rows, start. Every
// choice saves on its own, optimistically, through one lane so two quick changes never race
// into a stale version (CLAUDE.md §5a). A saved matching for these columns arrives filled in,
// so most re-imports are one click on "Start import".

/** Select values that can't be a column's name, which could be anything ("CV", "none"…). */
const NOT_MATCHED = '__not_matched__';
const CV_TEXT_ONLY = '__cv_text_only__';

/** ADR 0062 §7: what the matched columns are for now that every job ranks by its ideal candidate. */
const FACT_COLUMNS_NOTE = 'Years, education, city, notice and salary columns feed the profile’s facts;';
const CV_READ_NOTE = 'Jev reads the CV for the rest.';

export interface ColumnRow {
  readonly header: string;
  readonly example: string;
  readonly target: ImportTarget | null;
}

/** What the screen shows while a save is on its way: the change, and what it means for Start. */
function optimistic(view: ImportView, change: MappingChange): ImportView {
  return {
    ...view,
    ...change,
    problems: mappingProblems(change.columns),
    coverage: view.coverage.map((row) => ({ ...row, column: change.criteria[row.key] ?? null })),
  };
}

/**
 * Redesign Group 3 (Job-import.dc.html): while no column is the name, the pickers of the columns
 * that look like one ("Full Name", "Applicant name") read red, so the eye goes to the fix.
 */
export function isNamePickerInvalid(view: ImportView, row: ColumnRow): boolean {
  return view.problems.name !== undefined && row.target === null && normaliseHeader(row.header).split(' ').includes('name');
}

function exampleOf(view: ImportView, index: number): string {
  return view.preview.rows.map((row) => row[index] ?? '').find((value) => value.trim() !== '') ?? '';
}

export function ImportSetup({ view, onOpen }: { readonly view: ImportView; readonly onOpen: (importId: string | null) => void }): ReactNode {
  const queryClient = useQueryClient();
  const show = useToasts((toasts) => toasts.show);
  const sources = useSourceOptions('record');
  const [lane] = useState<SaveLane>(() => createSaveLane(view.version));
  const [isStarting, setIsStarting] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);
  useEffect(() => lane.follow(view.version), [lane, view.version]);

  const current = (): ImportView => queryClient.getQueryData<ImportView>(importKey(view.id)) ?? view;

  const apply = async (change: Partial<MappingChange>): Promise<void> => {
    const before = current();
    const next: MappingChange = { columns: change.columns ?? before.columns, criteria: change.criteria ?? before.criteria, source: change.source ?? before.source };
    queryClient.setQueryData(importKey(view.id), optimistic(before, next));
    try {
      const saved = await lane.run((version) => saveImportMapping(view.id, next, version));
      queryClient.setQueryData(importKey(view.id), saved);
    } catch (error) {
      queryClient.setQueryData(importKey(view.id), before);
      show({ tone: 'danger', message: "Couldn't save the matching", detail: error instanceof Error ? error.message : 'Try again.' });
      throw error;
    }
  };

  /** One field per column: choosing a field another column had moves it here (fewer clicks than refusing). */
  const matchColumn = (header: string, target: ImportTarget | null): Promise<void> => {
    const columns = Object.fromEntries(
      Object.entries(current().columns).map(([other, value]) => {
        if (other === header) return [other, target];
        return [other, target !== null && value === target ? null : value];
      }),
    );
    return apply({ columns });
  };

  const chooseSheet = async (sheet: string): Promise<void> => {
    try {
      const next = await lane.run((version) => chooseImportSheet(view.id, sheet, version));
      queryClient.setQueryData(importKey(view.id), next);
    } catch (error) {
      show({ tone: 'danger', message: "Couldn't read that sheet", detail: error instanceof Error ? error.message : 'Try again.' });
    }
  };

  const start = async (): Promise<void> => {
    setIsStarting(true);
    const before = current();
    try {
      await lane.idle();
      // Optimistic: the progress screen appears at once; the rows start in the background.
      queryClient.setQueryData(importKey(view.id), { ...before, status: 'importing', summary: 'Starting…' });
      const started = await startImport(view.id, lane.version());
      queryClient.setQueryData(importKey(view.id), started);
      await queryClient.invalidateQueries({ queryKey: [...IMPORTS_KEY, 'list'] });
    } catch (error) {
      queryClient.setQueryData(importKey(view.id), before);
      setIsStarting(false);
      show({ tone: 'danger', message: "Couldn't start the import", detail: error instanceof Error ? error.message : 'Try again.' });
    }
  };

  const discard = async (): Promise<void> => {
    setIsDiscarding(true);
    try {
      await lane.idle();
      await discardImport(view.id, lane.version());
      await queryClient.invalidateQueries({ queryKey: [...IMPORTS_KEY, 'list'] });
      show({ tone: 'info', message: 'Import discarded', detail: 'Nothing from that file was imported.' });
      onOpen(null);
    } catch (error) {
      setIsDiscarding(false);
      show({ tone: 'danger', message: "Couldn't discard the import", detail: error instanceof Error ? error.message : 'Try again.' });
    }
  };

  const problem = view.problems.name ?? view.problems.contact ?? null;
  const columnRows: readonly ColumnRow[] = view.preview.headers.map((header, index) => ({ header, example: exampleOf(view, index), target: view.columns[header] ?? null }));

  return (
    <section className={styles.panel} aria-labelledby="import-setup-title">
      <div className={styles.setupHead}>
        <span className={styles.fileTile} aria-hidden>
          <FileSpreadsheet size={18} />
        </span>
        <div className={styles.setupTitle}>
          <h2 id="import-setup-title" className={styles.title}>
            {view.fileName}
          </h2>
          <p className={styles.lead}>Check how the columns are matched, then start. Columns left unmatched are kept on each candidate as Imported details.</p>
        </div>
        <ToneChip tone={IMPORT_STATUS_TONES[view.status]}>{IMPORT_STATUS_LABELS[view.status]}</ToneChip>
      </div>

      {view.isSavedMatching && (
        <div className={styles.banner} data-tone="done">
          <span>
            <strong>Saved matching applied.</strong> These columns were matched the same way the last time a file like this came from here.
          </span>
        </div>
      )}

      <div className={styles.settingsRow}>
        <div className={styles.sourceField}>
          <Field label="Where the file came from">
            {({ inputId }) => <Select id={inputId} label="Where the file came from" options={sources} value={view.source} onChange={(source) => void apply({ source }).catch(() => undefined)} />}
          </Field>
        </div>
        {view.sheets.length > 1 && (
          <div className={styles.sourceField}>
            <Field label="Sheet">
              {({ inputId }) => <Select id={inputId} label="Sheet" options={view.sheets.map((sheet) => ({ value: sheet, label: sheet }))} value={view.sheetName} onChange={(sheet) => void chooseSheet(sheet)} />}
            </Field>
          </div>
        )}
      </div>

      <div className={styles.block}>
        <h3 className={styles.subtitle}>Match the columns</h3>
        <EditableTable<ColumnRow>
          label="Match the columns"
          columns={matchingColumns((row) => isNamePickerInvalid(view, row))}
          rows={columnRows}
          rowKey={(row) => row.header}
          rowLabel={(row) => row.header}
          onCommit={(change) => matchColumn(change.row.header, change.row.target)}
        />
        <p className={styles.note}>
          <strong>{FACT_COLUMNS_NOTE}</strong> {CV_READ_NOTE}
        </p>
        {problem && (
          <p className={styles.problem} role="alert">
            {[view.problems.name, view.problems.contact].filter(Boolean).join(' ')}
          </p>
        )}
      </div>

      {/* ADR 0062 §3: the coverage check belongs to Screening criteria; off, the section is not drawn at all (#13). */}
      <ModuleGate module="screening_criteria">
        {view.coverage.length > 0 && (
          <div className={styles.block}>
            <h3 className={styles.subtitle}>Coverage check</h3>
            <p className={styles.muted}>Where each of the job's criteria will be read from. A criterion read only from the CV's text counts for less.</p>
            <EditableTable<ImportCoverageRow>
              label="Coverage check"
              columns={coverageColumns(view.preview.headers)}
              rows={view.coverage}
              rowKey={(row) => row.key}
              rowLabel={(row) => row.label}
              onCommit={(change) => apply({ criteria: { ...current().criteria, [change.row.key]: change.row.column } })}
            />
          </div>
        )}
      </ModuleGate>

      <div className={styles.block}>
        <h3 className={styles.subtitle}>Preview</h3>
        <p className={styles.counts}>
          <span>
            <strong>{formatNumber(view.preview.rowCount)}</strong> rows
          </span>
          <span>
            <strong>{formatNumber(view.preview.noCvLink)}</strong> have no CV link
          </span>
        </p>
        <PreviewPeople people={view.preview.people} />
        <div className={styles.previewScroll} tabIndex={0} aria-label="The first rows of the file">
          <table className={styles.preview}>
            <thead>
              <tr>
                {view.preview.headers.map((header) => (
                  <th key={header} scope="col">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {view.preview.rows.map((row, index) => (
                <tr key={index}>
                  {row.map((cell, column) => (
                    <td key={column}>{cell === '' ? '—' : cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className={styles.stickyBar} role="region" aria-label="Start the import">
        <span className={styles.stickyText} data-problem={problem !== null || undefined}>{problem ?? `${formatNumber(view.preview.rowCount)} ${view.preview.rowCount === 1 ? 'row' : 'rows'} will import in the background. You can leave this page; you'll be told when it's done.`}</span>
        <div className={styles.stickyActions}>
          <Button variant="ghost" isPending={isDiscarding} onClick={() => void discard()}>
            Discard
          </Button>
          <Button variant="solid" isPending={isStarting} disabled={problem !== null} onClick={() => void start()}>
            Start import
          </Button>
        </div>
      </div>
    </section>
  );
}

function matchingColumns(isInvalid: (row: ColumnRow) => boolean): readonly EditableColumn<ColumnRow>[] {
  return [
    { key: 'header', header: 'Column in the file', width: '30%', cell: { kind: 'display', render: (row) => <span className={styles.strong}>{row.header}</span> } },
    { key: 'example', header: 'Example', width: '35%', cell: { kind: 'display', render: (row) => <span className={styles.example}>{row.example === '' ? '—' : row.example}</span> } },
    {
      key: 'target',
      header: 'Field',
      width: '35%',
      isInvalid,
      cell: {
        kind: 'select',
        options: [{ value: NOT_MATCHED, label: 'Not matched: kept as Imported details' }, ...IMPORT_TARGETS.map((target) => ({ value: target, label: IMPORT_TARGET_LABELS[target] }))],
        value: (row) => row.target ?? NOT_MATCHED,
        update: (row, value) => ({ ...row, target: isImportTarget(value) ? value : null }),
      },
    },
  ];
}

function coverageColumns(headers: readonly string[]): readonly EditableColumn<ImportCoverageRow>[] {
  return [
    { key: 'label', header: 'Criterion', width: '28%', cell: { kind: 'display', render: (row) => <span className={styles.strong}>{row.label}</span> } },
    { key: 'importance', header: 'Importance', width: '16%', cell: { kind: 'display', render: (row) => CRITERION_IMPORTANCE_LABELS[row.importance] } },
    {
      key: 'column',
      header: 'Read from',
      width: '30%',
      cell: {
        kind: 'select',
        options: [{ value: CV_TEXT_ONLY, label: "Only the CV's text" }, ...headers.map((header) => ({ value: header, label: header }))],
        value: (row) => row.column ?? CV_TEXT_ONLY,
        update: (row, value) => ({ ...row, column: value === CV_TEXT_ONLY ? null : value }),
      },
    },
    { key: 'note', header: 'What this means', width: '26%', cell: { kind: 'display', render: (row) => <span className={row.column ? styles.muted : styles.weaker}>{coverageNote(row.kind, row.column)}</span> } },
  ];
}
